import { memo } from "react";
import { effectiveQtyLimit, useTrading, type QtyLimit } from "@helm/core";
import { shallowEqual, useSlice } from "../viewmodel/slice";

const sameLimit = (a: QtyLimit | null, b: QtyLimit | null): boolean =>
  a === null || b === null ? a === b : shallowEqual(a, b);

/** Personal operational cap only. Broker limits require a separate validated coverage contract.
 * This presentation does not change effectiveQtyLimit used by existing order confirmations. */
export const QtyLimitBadge = memo(function QtyLimitBadge({
  accountId,
  qty,
}: {
  accountId: string | null;
  qty: number;
}): React.JSX.Element | null {
  // The limit, not the RiskState: its P&L fields change with every quote while a position is open.
  const lim = useSlice(
    useTrading.subscribe,
    useTrading.getState,
    (s) => {
      const risk = accountId === null ? undefined : s.risk.get(accountId);
      return effectiveQtyLimit({ maxOrderQty: risk?.maxOrderQty, maxPosition: risk?.maxPosition });
    },
    sameLimit,
  );
  if (lim === null) return null;
  const over = qty > lim.max;
  return (
    <span
      className={over ? "qty-limit over" : "qty-limit"}
      data-max={lim.max}
      data-source={lim.source}
      title={lim.title}
    >
      TradePrizm max {lim.max}
      <span className="qty-limit-src">personal</span>
    </span>
  );
});
